/**
 * The document every route is rendered into.
 *
 * This is the old `index.html`, now written in Solid so the head can change
 * with the route: the deck wants a locked, unzoomable viewport, and a
 * document wants the opposite. Route heads are merged leaf-first, so anything
 * a page states here wins over the defaults below.
 *
 * Nothing app-specific belongs in this file. It is the one module every route
 * loads, so an import here is an import a policy page pays for.
 */

import { QueryClientProvider } from '@tanstack/solid-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useMatches,
  useRouter,
} from '@tanstack/solid-router';
import { BUILD } from '../lib/version.ts';
import type { RouterContext } from '../router.tsx';
import '../styles/global.css';

declare module '@tanstack/solid-router' {
  interface StaticDataRouteOption {
    /**
     * Prose rather than deck. The shell locks scrolling, gestures and zoom
     * for the turntable's sake; a route that sets this gets all three back.
     */
    document?: boolean;
  }
}

/** The deck's viewport: fixed scale, no pinch, and drawn under the notch. */
const DECK_VIEWPORT =
  'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no';

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: DECK_VIEWPORT },
      { name: 'theme-color', content: '#F4F7F5' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'description', content: 'A 3D vinyl turntable player for YouTube Music.' },
      /* Which build served this page, readable from view-source on any route.
         It costs one import of a module that `define` has already folded down
         to constants, so a policy page pays nothing for it. */
      { name: 'build-version', content: BUILD.version },
      { name: 'build-commit', content: BUILD.commit },
      { title: 'Turntable' },
    ],
    links: [
      { rel: 'icon', href: '/favicon.ico', sizes: '16x16 32x32 48x48' },
      { rel: 'icon', href: '/assets/logo.svg', type: 'image/svg+xml' },
      { rel: 'apple-touch-icon', href: '/assets/apple-touch-icon.png' },
    ],
  }),
  component: RootDocument,
});

function RootDocument() {
  const router = useRouter();
  const matches = useMatches();
  const route = (): string | undefined =>
    matches().some((m) => m.staticData.document) ? 'doc' : undefined;

  return (
    <html lang="en" data-route={route()}>
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={router.options.context.queryClient}>
          <Outlet />
        </QueryClientProvider>
        <noscript>This app needs JavaScript enabled.</noscript>
        <Scripts />
      </body>
    </html>
  );
}
